import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import puzzlePlanetLogo from '../images/logos/title.webp';
import spaceImage from '../images/space.webp';
import { authApi } from '../services/api';
import HostedInvoiceDialog from './HostedInvoiceDialog';
import ActivatingTrialOverlay from './ProcessingTrialOverlay';
import TermsAndPrivacy from './TermsAndPrivacy';
import TrialCalloutBanner from './TrialCalloutBanner';
import TrialPaymentDescription from './TrialPaymentDescription';

const CheckoutForm: React.FC<{
    onSuccess: (result: any) => void;
    onError: (error: string) => void;
    isProcessing: boolean;
    setIsProcessing: (processing: boolean) => void;
    setHostedInvoiceData: (data: { url: string } | null) => void;
}> = ({ onSuccess, onError, isProcessing, setIsProcessing, setHostedInvoiceData }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentInfoComplete, setIsPaymentInfoComplete] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        try {
            // Set processing state to show spinner and disable button
            setIsProcessing(true);

            await elements.submit();

            // Create payment method
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                elements,
            });

            if (pmError) {
                onError(pmError.message || 'Failed to create payment method');
                return;
            }

            if (!paymentMethod) {
                onError('Failed to create payment method');
                return;
            }

            // Call attemptSubscriptionCreationExistingUser with the payment method ID
            const response = await authApi.attemptSubscriptionCreationExistingUser({
                paymentMethodId: paymentMethod.id,
            });

            if (response.hostedInvoiceUrl) {
                console.log('Hosted invoice URL:', response.hostedInvoiceUrl);
                // Show dialog instead of redirecting
                setHostedInvoiceData({
                    url: response.hostedInvoiceUrl,
                });
                return;
            }

            if (response.status === 'success') {
                onSuccess(response);
            } else {
                onError(response.message || 'Failed to create subscription');
            }
        } catch (err: any) {
            onError(err.message || 'An unexpected error occurred');
        } finally {
            // Reset processing state
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className='space-y-6'>
                <div className='mb-6'>
                    <h3
                        className='text-xl md:text-2xl font-semibold mb-2'
                        style={{ color: '#FFFFFF' }}
                    >
                        Payment Information
                    </h3>
                    <TrialPaymentDescription style={{ color: '#FFFFFF' }} />
                </div>

                <div className='bg-white rounded-lg p-6'>
                    <PaymentElement
                        className='mb-6'
                        onChange={event => {
                            setIsPaymentInfoComplete(event.complete);
                        }}
                    />
                </div>

                <TrialCalloutBanner />

                <button
                    type='submit'
                    disabled={!stripe || !elements || isProcessing || !isPaymentInfoComplete}
                    className='w-full px-8 py-4 text-lg font-semibold bg-gradient-to-b from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                    onClick={handleSubmit}
                >
                    Activate Trial
                </button>
                <TermsAndPrivacy />

                {/* Help Text */}
                <p className='mt-4 text-center text-white text-sm'>
                    If you have any issues during signup, please email{' '}
                    <a
                        href='mailto:support@mindfulgames.io'
                        className='text-orange-400 hover:text-orange-300 underline transition-colors duration-200'
                    >
                        support@mindfulgames.io
                    </a>
                </p>

                {/* Show what fields are still needed */}
                {!isPaymentInfoComplete && (
                    <div className='mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                        <div className='flex items-start'>
                            <svg
                                className='w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                                ></path>
                            </svg>
                            <div className='text-sm text-amber-800'>
                                <p className='font-medium mb-1'>
                                    Complete payment information to proceed:
                                </p>
                                <ul className='space-y-1 text-xs'>
                                    <li>• Enter your payment details above</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ActivatingTrialOverlay isVisible={isProcessing} />
        </>
    );
};

const TrialSignupPayment: React.FC = () => {
    const [error, setError] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [successData, setSuccessData] = useState<any>(null);
    const [hostedInvoiceData, setHostedInvoiceData] = useState<{
        url: string;
    } | null>(null);

    const handlePaymentSuccess = (result: any) => {
        setSuccessData(result);
        // Clear URL parameters after successful payment
        window.history.replaceState({}, document.title, window.location.pathname);
    };

    const handlePaymentError = (errorMessage: string) => {
        setError(errorMessage);
        setIsProcessing(false);
    };

    // Show hosted invoice dialog
    if (hostedInvoiceData) {
        return <HostedInvoiceDialog hostedInvoiceData={hostedInvoiceData} />;
    }

    if (successData) {
        return (
            <div
                className='min-h-screen flex flex-col items-center justify-center py-12 px-4 overflow-x-hidden w-full'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='flex flex-col items-center space-y-8 max-w-2xl w-full'>
                    {/* Title Logo */}
                    <div className='flex justify-center mb-8'>
                        <img
                            src={puzzlePlanetLogo}
                            alt='Puzzle Planet'
                            className='h-24 md:h-32 w-auto'
                        />
                    </div>

                    <div className='max-w-md w-full mx-auto p-8 bg-white rounded-2xl shadow-xl border border-gray-100 text-center'>
                        <div className='mb-6'>
                            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <svg
                                    className='w-8 h-8 text-green-600'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M5 13l4 4L19 7'
                                    ></path>
                                </svg>
                            </div>
                        </div>
                        <h2 className='text-2xl font-bold mb-2' style={{ color: '#FFB502' }}>
                            Trial Activated!
                        </h2>
                        <p className='text-gray-600 mb-6'>
                            Your 7-day free trial has been activated successfully. Welcome to Puzzle
                            Planet!
                        </p>
                        <button
                            onClick={() =>
                                (window.location.href = '/game?t=' + new Date().getTime())
                            }
                            className='w-full px-8 py-4 text-lg font-semibold bg-gradient-to-b from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200'
                        >
                            Start Playing
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className='min-h-screen flex flex-col items-center justify-center py-12 px-4 overflow-x-hidden w-full'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='flex flex-col items-center space-y-8 max-w-2xl w-full'>
                {/* Title Logo */}
                <div className='flex justify-center mb-8'>
                    <img
                        src={puzzlePlanetLogo}
                        alt='Puzzle Planet'
                        className='h-24 md:h-32 w-auto'
                    />
                </div>

                {/* Step Indicator */}
                <div className='text-center mb-4'>
                    <p className='text-xl md:text-2xl font-semibold' style={{ color: '#FFB502' }}>
                        Step 3/3
                    </p>
                </div>

                {/* Heading */}
                <h1
                    className='text-4xl md:text-5xl font-bold text-center'
                    style={{ color: '#FFB502' }}
                >
                    Activate your trial
                </h1>

                {/* Payment Form */}
                <div className='w-full max-w-md'>
                    {error && (
                        <div className='mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                            <div className='flex items-center'>
                                <svg
                                    className='w-5 h-5 mr-2'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                    ></path>
                                </svg>
                                {error}
                            </div>
                        </div>
                    )}

                    <CheckoutForm
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        isProcessing={isProcessing}
                        setIsProcessing={setIsProcessing}
                        setHostedInvoiceData={setHostedInvoiceData}
                    />
                </div>
            </div>
        </div>
    );
};

export default TrialSignupPayment;
